/**
 * B-24b WS-3 — Integrations summary panel for the Operator Console.
 *
 * Reuses GET /admin/dashboard/integrations (existing endpoint) at its current
 * range-agnostic snapshot, rendering the sync health summary (healthy /
 * degraded / failing counts). Does NOT fork the existing IntegrationsPanel
 * component — it builds a lighter summary for the front door.
 *
 * Low-data: empty sync_health or all-zero counts → intentional
 * "no integrations configured" message, not empty/blank.
 */
import { useQuery } from "@tanstack/react-query";
import {
  fetchIntegrationsPanel,
  type IntegrationsPanelResponse,
} from "@/lib/dashboardApi";

export function OperatorIntegrationsPanel() {
  const { data, isLoading, isError } = useQuery<
    IntegrationsPanelResponse,
    Error
  >({
    queryKey: ["admin", "dashboard", "integrations", "30d"],
    queryFn: () => fetchIntegrationsPanel("30d"),
    staleTime: 60_000,
  });

  const syncHealth = data?.sync_health ?? [];
  const total = syncHealth.reduce((s, h) => s + h.count, 0);
  const byStatus: Record<string, number> = {};
  for (const h of syncHealth) {
    byStatus[h.status] = (byStatus[h.status] ?? 0) + h.count;
  }
  const healthy = byStatus["ok"] ?? byStatus["healthy"] ?? 0;
  const degraded = byStatus["degraded"] ?? 0;
  const failing =
    byStatus["failing"] ??
    byStatus["error"] ??
    byStatus["failed"] ??
    0;
  const kpis = data?.kpis;

  return (
    <section
      data-testid="operator-integrations-panel"
      className="rounded-lg border border-border-default bg-surface-card p-4"
    >
      <div className="flex items-baseline justify-between">
        <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-text-tertiary">
          Reliability
        </p>
      </div>
      <h3 className="mt-0.5 text-sm font-semibold text-text-primary">
        Integrations
      </h3>
      <div className="mt-3">
        {isLoading && (
          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="h-6 animate-pulse rounded bg-surface-canvas"
              />
            ))}
          </div>
        )}
        {isError && (
          <p className="text-xs text-brand-negative">
            Couldn't load integrations.
          </p>
        )}
        {data && total === 0 && (
          <p
            data-testid="integrations-empty"
            className="text-xs text-text-tertiary"
          >
            No integrations configured yet.
          </p>
        )}
        {data && total > 0 && (
          <div className="space-y-2">
            {[
              {
                label: "Healthy",
                v: healthy,
                cls: "text-brand-positive",
              },
              {
                label: "Degraded",
                v: degraded,
                cls: degraded > 0 ? "text-brand-warn" : "text-text-secondary",
              },
              {
                label: "Failing",
                v: failing,
                cls: failing > 0 ? "text-brand-negative" : "text-text-secondary",
              },
            ].map((r) => (
              <div key={r.label} className="flex items-center justify-between text-xs">
                <span className="text-text-tertiary">{r.label}</span>
                <span className={"font-mono tabular-nums font-semibold " + r.cls}>
                  {r.v}
                </span>
              </div>
            ))}
            {kpis && (
              <div className="border-t border-border-default pt-2 text-2xs text-text-tertiary">
                {kpis.tenants_with_integration} of{" "}
                {kpis.active_tenants} tenants integrated
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
